import { useState, useCallback, useRef } from 'react';
import SEO from '../components/SEO';
import useFadeIn from '../hooks/useFadeIn';
import './Databook.css';

const editions = [
  {
    key: 'third',
    tab: 'Third Edition (2025)',
    title: 'Social and Economic Changes in American Indian Reservations: A Databook of the US Census and the American Community Survey, Third Edition, 1990\u20132020',
    authors: ['Randall Akee', 'Elijah Moreno', 'Amy Besaw Medford'],
    institution: 'Ash Center for Democratic Governance and Innovation, Harvard Kennedy School',
    description: 'The third edition extends the series through 2020, drawing on three decades of Census and American Community Survey data to measure how life on reservations has changed across fourteen socioeconomic indicators. We document real gains such as lower unemployment, rising educational attainment, and improved housing, while showing that deep gaps between reservation populations and the rest of the country persist. The expanded time horizon lets researchers and policymakers see which trends held, which reversed, and where progress stalled.',
    coAuthor: true,
    href: 'https://ash.harvard.edu/wp-content/uploads/2025/09/Databook-Third-Edition-2025-09-07-1.pdf',
    color: 'var(--color-about)',
    cite: `Akee, Randall, Elijah Moreno, and Amy Besaw Medford. 2025.\n"Social and Economic Changes in American Indian Reservations:\nA Databook of the US Census and the American Community Survey,\nThird Edition, 1990\u20132020." Cambridge, MA: Ash Center for\nDemocratic Governance and Innovation, Harvard Kennedy School.`,
  },
  {
    key: 'second',
    tab: 'Second Edition (2014)',
    title: 'Social and Economic Change on American Indian Reservations: A Databook of the US Censuses and the American Community Survey, 1990\u20132010',
    authors: ['Randall K.Q. Akee', 'Jonathan B. Taylor'],
    institution: 'Taylor Policy Group / Harvard Project on American Indian Economic Development',
    description: 'The second edition brought the series into the 2010 Census era and introduced American Community Survey data, allowing finer-grained tracking of income, employment, and education on reservations. It showed that while socioeconomic conditions continued to improve in absolute terms, the pace of change was slowing and income gaps relative to the broader U.S. population remained large.',
    coAuthor: false,
    href: 'https://static1.squarespace.com/static/52557b58e4b0d4767401ce95/t/5379756ce4b095f55e75c77b/1400468844624/AkeeTaylorUSDatabook2014-05-15.pdf',
    color: 'var(--color-about)',
    cite: null,
  },
  {
    key: 'first',
    tab: 'First Edition (2005)',
    title: 'American Indians on Reservations: A Databook of Socioeconomic Change Between the 1990 and 2000 Censuses',
    authors: ['Jonathan B. Taylor', 'Joseph P. Kalt'],
    institution: 'Harvard Project on American Indian Economic Development, Harvard Kennedy School',
    description: 'This is the volume that started the series. Taylor and Kalt compiled 1990 and 2000 Census data across fifteen socioeconomic measures for every reservation in the lower 48 states, comparing gaming and non-gaming areas to each other and to the U.S. as a whole. It established a baseline showing that rapid economic development was underway in Indian Country, but that substantial gaps remained.',
    coAuthor: false,
    href: 'https://indigenousgov.hks.harvard.edu/sites/g/files/omnuum6806/files/hpaied/files/americanindiansonreservationsadatabookofsocioeconomicchange.pdf',
    color: 'var(--color-about)',
    cite: null,
  },
];

export default function Databook() {
  const [active, setActive] = useState(0);
  const [copied, setCopied] = useState(false);
  const pageRef = useRef(null);
  useFadeIn(pageRef);
  const ed = editions[active];

  const copyCitation = useCallback(() => {
    if (!ed.cite) return;
    navigator.clipboard.writeText(ed.cite);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [ed]);

  return (
    <main id="main-content" ref={pageRef}>
      <SEO
        title="Databooks"
        description="American Indians on Reservations: A Databook of socioeconomic change across three editions (2005, 2014, 2025) from the Harvard Project on American Indian Economic Development."
        path="/databook"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'ScholarlyArticle',
          name: 'Social and Economic Changes in American Indian Reservations: A Databook of the US Census and the American Community Survey, Third Edition, 1990\u20132020',
          author: [
            { '@type': 'Person', name: 'Randall Akee' },
            { '@type': 'Person', name: 'Elijah Moreno' },
            { '@type': 'Person', name: 'Amy Besaw Medford' },
          ],
          datePublished: '2025',
          publisher: {
            '@type': 'Organization',
            name: 'Ash Center for Democratic Governance and Innovation, Harvard Kennedy School',
          },
          about: ['American Indian reservations', 'socioeconomic change', 'US Census', 'American Community Survey'],
          url: 'https://elijahmoreno.com/databook',
        }}
      />

      <section className="section section--first">
        <div className="container--narrow">
          <p className="databook__label fade-in">Featured Publication</p>
          <h1 className="section-title databook__title fade-in fade-in--delay-1">
            American Indians on Reservations
          </h1>
          <p className="databook__subtitle fade-in fade-in--delay-2">
            A Databook of Socioeconomic Change
          </p>
          <div className="section-body fade-in fade-in--delay-3">
            <p>
              This series tracks socioeconomic conditions across American Indian reservations
              in the lower 48 states using U.S. Census and American Community Survey data. I
              co-authored the third edition with Randall Akee and Amy Besaw Medford. All three
              editions are collected here for researchers, policymakers, and tribal leaders.
            </p>
          </div>

          <div className="databook__tabs fade-in fade-in--delay-4" role="tablist" aria-label="Databook editions">
            {editions.map((e, i) => (
              <button
                key={e.key}
                role="tab"
                aria-selected={i === active}
                className={`databook__tab ${i === active ? 'databook__tab--active' : ''}`}
                style={{ '--tab-color': e.color }}
                onClick={() => setActive(i)}
              >
                {e.tab}
              </button>
            ))}
          </div>

          <div
            className="databook__panel"
            role="tabpanel"
            aria-label={ed.tab}
            style={{ '--card-color': ed.color }}
          >
            {ed.coAuthor && (
              <span className="databook__badge">Co-Author</span>
            )}
            <h2 className="databook__panel-title">{ed.title}</h2>
            <p className="databook__authors">
              {ed.authors.map((author, j) => (
                <span key={author}>
                  {author === 'Elijah Moreno' ? <strong>{author}</strong> : author}
                  {j < ed.authors.length - 1 ? ', ' : ''}
                </span>
              ))}
            </p>
            <p className="databook__institution">{ed.institution}</p>
            <p className="databook__description">{ed.description}</p>
            <a
              href={ed.href}
              target="_blank"
              rel="noopener noreferrer"
              className="databook__cta"
            >
              View PDF
            </a>

            {ed.cite && (
              <div className="databook__cite-section">
                <div className="databook__cite-header">
                  <h3 className="databook__cite-heading">Citation</h3>
                  <button
                    className="databook__copy-btn"
                    onClick={copyCitation}
                    aria-label="Copy citation to clipboard"
                  >
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <pre className="databook__cite"><code>{ed.cite}</code></pre>
              </div>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}
